<script setup>
import { reactive, ref, watch, onMounted, computed } from "vue";
import { dateTime, justDate } from "@/helpers/dateTimeHelpers.js";
import CompletedWorksFilter from "./CompletedWorksFilter.vue";
import moment from "moment";
import axios from "axios";
import TableRowDataLoader from "@/components/shared/loader/TableRowDataLoader.vue";

const props = defineProps(["completedData"]);
const offset = ref(3);
const pagination = ref(null);
const trees = ref(null);
const loading = ref(true);

const sortBy = ref("estimated_date");
const sortAsc = ref(true);
const works = ref([]);
const tasks = ref(props.completedData.workTasks || []);
const attachments = ref(props.completedData.attachments || {});
const groupByDate = ref(false);
const groupByUser = ref(false);
const updatedBetween = ref(null);
const selectedFilters = ref({});
const filter = reactive({
    trees_species_id: null,
    trees_status_id: null,
    trees_ule_id: null,
    trees_risk_id: null,
    trees_age_id: null,
    trees_health_id: null,
    trees_height_id: null,
    trees_structure_id: null,
    trees_canopy_id: null,
    trees_circumference_id: null,
    trees_dbh_id: null,
    work_tasks_done: null,
    work_tasks_required: null,
    work_mutate: null,
    works_id: null,
    trees_tag_id: null,
    equipment_id: null,
    storm_event: null,
});

const trees_maintenance = reactive({
    status: null,
    user_id: null,
    work_tasks_done: null,
});
const trees_inspection = reactive({
    work_complete: null,
    user_id: null,
    work_tasks: null,
});

onMounted(() => {
    getFilteredData();
});

watch([selectedFilters], async () => {
    updatedBetween.value = selectedFilters.value["updatedBetween"];
    filter.equipment_id = selectedFilters.value["equipment"];
    filter.works_id = selectedFilters.value["works"];
    filter.trees_tag_id = selectedFilters.value["tags"];
    filter.storm_event = selectedFilters.value["storm"];
    getFilteredData();
});

const getFilteredData = (noMap = false) => {
    axios
        .post("/api/reporting/works?" + queryString() + "&users_only=1" + "&getMap=nomap")
        .then((response) => {
            pagination.value = response.data.trees;
            works.value = response.data.trees.data;
            trees.value = response.data.map_trees;
            loading.value = false;
        })
        .catch((error) => {
            console.log(error);
        });
};

const queryString = (getMapData = true) => {
    var q = "";

    let keys = Object.keys(filter);
    for (let key of keys) {
        if (filter[key]) {
            var val = "";
            if (Array.isArray(filter[key])) {
                filter[key].forEach((e, i) => {
                    if (i !== 0) {
                        val = val + ",";
                    }
                    val = val + e;
                });
            } else {
                val = filter[key];
            }
            q = q + "filter[" + key + "]=" + val + "&";
        }
    }

    if (filter.storm_event === false) {
        q = q + "filter[storm_event]=" + false + "&";
    }

    if (
        selectedFilters.value["updatedBetween"] &&
        selectedFilters.value["updatedBetween"].start &&
        selectedFilters.value["updatedBetween"].end
    ) {
        q =
            q +
            "filter[updated_between]=" +
            moment(selectedFilters.value["updatedBetween"].start).format("YYYY-MM-DD") +
            "," +
            moment(selectedFilters.value["updatedBetween"].end).format("YYYY-MM-DD") +
            "&";
    }

    let works = Object.keys(trees_maintenance);
    for (let key of works) {
        if (trees_maintenance[key]) {
            var val = "";
            trees_maintenance[key].forEach((e, i) => {
                if (i !== 0) {
                    val = val + ",";
                }
                val = val + e;
            });
            q = q + "filter[trees_maintenance." + key + "]=" + val + "&";
        }
    }

    let inspections = Object.keys(trees_inspection);
    for (let key of inspections) {
        if (trees_inspection[key]) {
            var val = "";
            trees_inspection[key].forEach((e, i) => {
                if (i !== 0) {
                    val = val + ",";
                }
                val = val + e;
            });
            q = q + "filter[" + key + "]=" + val + "&";
        }
    }

    q = q + "&groupByDate=" + groupByDate.value + "&groupByUser=" + groupByUser.value + "&";

    return q;
};

const getWorkTask = (taskId) => {
    if (parseInt(taskId)) {
        var length = tasks.value.length;
        for (var i = 0; i < length; i++) {
            if (tasks.value[i].id === parseInt(taskId)) {
                return tasks.value[i].task;
            }
        }
    }
};

const getPriority = (rid) => {
    if (rid == 1) {
        return "Low";
    } else if (rid == 2) {
        return "Medium";
    } else if (rid == 3) {
        return "High";
    } else {
        return "";
    }
};

const getAvatarBackgroundColour = (name) => {
    if (name.includes("Green")) {
        return "#4caf50";
    } else if (name.includes("Red")) {
        return "#ff5722";
    } else if (name.includes("Blue")) {
        return "#1367bf";
    } else if (name.includes("Yellow")) {
        return "#eaf31a";
    } else if (name.includes("Orange")) {
        return "#ffaa49";
    } else if (name.includes("Purple")) {
        return "#df49ff";
    } else if (name.includes("Pink")) {
        return "#ff49a9";
    } else {
        return "";
    }
};

const handleEmitFilter = (filter) => {
    console.log("emit handler" + JSON.stringify(filter));
    selectedFilters.value = filter;
};

// Pagination code starts
const changePageNumber = (page) => {
    if (loading.value) {
        return;
    }
    loading.value = true;
    axios
        .post("/api/reporting/works?" + queryString(false) + "page=" + page + "&users_only=1" + "&getMap=nomap")
        .then((response) => {
            pagination.value = response.data.trees;
            works.value = response.data.trees.data;
            trees.value = response.data.map_trees;
            loading.value = false;
        })
        .catch((error) => {
            console.log(error);
        });
};

const changePageUrl = (url) => {
    if (loading.value) {
        return;
    }
    loading.value = true;
    axios
        .post(url)
        .then((response) => {
            pagination.value = response.data.trees;
            works.value = response.data.trees.data;
            trees.value = response.data.map_trees;
            loading.value = false;
        })
        .catch((error) => {
            console.log(error);
        });
};

const pages = computed(() => {
    if (!pagination.value.to) {
        return null;
    }
    let from = pagination.value.current_page - offset.value;
    if (from < 1) {
        from = 1;
    }
    let to = from + offset.value * 2;
    if (to >= pagination.value.last_page) {
        to = pagination.value.last_page;
    }
    let pages = [];
    for (let page = from; page <= to; page++) {
        pages.push(page);
    }
    return pages;
});
</script>

<template>
    <div>
        <!-- Completed works header  -->
        <div class="px-1 lg:px-8 flex flex-wrap items-center py-3">
            <div class="w-full lg:w-3/4">
                <p class="my-3 text-xl text-slate-600">Completed Works ({{ pagination?.total || 0 }})</p>
                <p class="my-3 text-md text-slate-500">Your completed Works</p>
            </div>
        </div>
        <div class="filters">
            <completed-works-filter :work-tasks="tasks" :attachments="attachments" @emit-filter="handleEmitFilter" />
        </div>

        <!--  Start completed work table below here  -->
        <div
            v-if="works && works.length && !loading"
            class="ftree-table striped hovered main-first-cell overflow-x-auto"
        >
            <div class="thead bg-slate-100 min-w-full">
                <div class="tr p-2">
                    <div class="td bg-slate-100 items-center w-1/2 lg:w-1/6">Tree ID</div>
                    <div class="td bg-slate-100 items-center w-1/2 lg:w-2/6">Tasks / NOTES</div>
                    <div
                        @click.prevent="
                            sortBy = 'assigned_date';
                            sortAsc = !sortAsc;
                        "
                        :class="sortBy === 'assigned_date' ? 'font-bold text-blue-600' : 'font-bold text-slate-600'"
                        class="td bg-slate-100 items-center w-1/2 lg:w-1/6 cursor-pointer uppercase p-2"
                    >
                        <div class="w-full flex items-center">
                            Assigned By
                            <svg
                                v-show="sortBy === 'assigned_date'"
                                :class="!sortAsc ? 'rotate-180' : ''"
                                class="inline h-5 w-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                    </div>
                    <div
                        @click.prevent="
                            sortBy = 'updated_at';
                            sortAsc = !sortAsc;
                        "
                        :class="sortBy === 'updated_at' ? 'font-bold text-blue-600' : 'font-bold text-slate-600'"
                        class="td bg-slate-100 items-center flex w-1/2 lg:w-1/6 cursor-pointer uppercase p-2"
                    >
                        Last Updated
                        <svg
                            v-show="sortBy === 'updated_at'"
                            :class="!sortAsc ? 'rotate-180' : ''"
                            class="inline h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </div>
                    <div
                        @click.prevent="
                            sortBy = 'estimated_date';
                            sortAsc = !sortAsc;
                        "
                        :class="sortBy === 'estimated_date' ? 'font-bold text-blue-600' : 'font-bold text-slate-600'"
                        class="td bg-slate-100 items-center flex w-1/2 lg:w-1/6 cursor-pointer uppercase"
                    >
                        Due Date
                        <svg
                            v-show="sortBy === 'estimated_date'"
                            :class="!sortAsc ? 'rotate-180' : ''"
                            class="inline h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </div>
                </div>
            </div>

            <!-- table body with assignment information -->
            <div class="tbody text-left text-slate-700 w-full">
                <div
                    v-for="(work, index) in works"
                    :key="index"
                    class="tr px-2 hover:bg-slate-100 cursor-pointer w-full"
                >
                    <div v-if="!work.is_unlinked" class="py-2 border-b border-slate-200 w-1/2 lg:w-1/6 items-center">
                        <a :href="'/maintenance-map?treeId=' + work.tree.id" class="text-slate-600 font-semibold mr-3"
                            >#{{ work.tree.id }}
                        </a>
                    </div>
                    <div class="py-2 border-b border-slate-200 w-1/2 lg:w-1/6 items-center" v-else>
                        <a
                            :href="'/maintenance-map?woId=' + work.id"
                            class="text-slate-600 font-semibold mr-3"
                            target="_blank"
                            >Unlinked</a
                        >
                        <div class="ml-2 py-2 font-semibold">
                            <span class="status" :class="getPriority(work.t1_works.forestree_priority)"></span>
                            {{ getPriority(work.t1_works.forestree_priority) }}
                        </div>
                    </div>
                    <div v-if="!work.is_unlinked" class="py-2 border-b border-slate-200 w-1/2 lg:w-2/6 items-center">
                        <div v-if="work.tree.address" class="font-semibold pb-1 text-slate-600 text-xs">
                            {{ work.tree.address }}
                        </div>
                        <div v-if="work.status === 'Complete'" class="w-full text-sm items-center">
                            <span
                                class="inline-block bg-green-500 rounded-full px-3 py-0.5 text-sm text-slate-200 mr-2 my-1"
                            >
                                Complete
                            </span>
                        </div>
                        <div v-if="work.works_as_list && work.works_as_list.length">
                            <span
                                v-for="(task, index) in work.works_as_list"
                                :key="index"
                                class="inline-block bg-slate-600 rounded-full px-3 py-0.5 mb-1 text-sm text-slate-200 mr-2"
                            >
                                {{ getWorkTask(task) }}
                            </span>
                        </div>
                        <div v-if="work.tree.city" class="w-full text-sm text-slate-600 items-center">
                            <span
                                class="inline-block bg-slate-200 rounded-full px-3 py-0.5 text-sm text-slate-600 mr-2"
                            >
                                {{ work.tree.city }}
                            </span>
                        </div>
                        <div
                            v-if="work.storm_event"
                            class="inline-block bg-slate-800 rounded-full px-1 py-0 text-sm text-slate-200 mt-2"
                        >
                            <div class="flex text-sm text-slate-100 font-semibold py-1 pr-2 flex flex-row items-center">
                                <img src="/images/icons/storm.png" class="h-6 w-6 mr-2" />
                                <p>Storm Event</p>
                            </div>
                        </div>
                        <div class="py-2 items-center text-slate-500">
                            {{ work.notes }}
                        </div>
                    </div>
                    <div v-else class="py-2 border-b border-slate-200 w-1/2 lg:w-2/6 items-center">
                        <div v-if="work.t1_works && work.t1_works.work_complete" class="w-full text-sm items-center">
                            <span
                                class="inline-block bg-green-500 rounded-full px-3 py-1 text-sm text-slate-200 mr-2 my-1"
                            >
                                Complete
                            </span>
                        </div>
                        <div>
                            <span
                                class="inline-block bg-slate-600 rounded-full px-3 py-1 mb-1 text-sm text-slate-200 mr-2"
                            >
                                {{ work.t1_works.woWorkTypeCode }}
                            </span>
                        </div>
                        <div class="py-2 tems-center text-slate-500">
                            {{ work.t1_works.woDescription }}
                        </div>
                    </div>

                    <div class="py-2 border-b border-slate-200 w-1/2 lg:w-1/6 items-center text-slate-500">
                        <div class="font-semibold pb-2 text-slate-600">
                            {{ dateTime(work.created_at) }}
                        </div>
                    </div>
                    <div
                        class="py-2 border-b border-slate-200 w-1/2 lg:w-1/6 items-center font-semibold pb-2 text-slate-600"
                    >
                        {{ dateTime(work.updated_at) }}
                    </div>
                    <div
                        class="py-2 border-b border-slate-200 w-1/2 lg:w-1/6 items-center font-semibold pb-2 text-slate-600"
                        v-if="!work.is_unlinked"
                    >
                        {{ dateTime(work.estimate_finish) }}
                    </div>
                    <div
                        class="py-2 border-b border-slate-200 w-1/2 lg:w-1/6 items-center font-semibold pb-2 text-slate-600"
                        v-else
                    >
                        {{ justDate(work.estimated_finish) }}
                    </div>
                </div>
            </div>
        </div>

        <!--    Pagination     -->
        <div
            v-if="pagination && pagination.last_page !== 1 && !loading"
            class="bg-white px-4 py-3 flex items-center justify-between border-t border-slate-200 sm:px-6"
        >
            <div class="flex-1 flex justify-between sm:hidden">
                <a
                    href="#"
                    @click.stop="changePageUrl(pagination.prev_page_url)"
                    class="relative inline-flex items-center px-4 py-2 border border-slate-300 text-sm leading-5 font-medium rounded-md text-slate-700 bg-white hover:text-slate-500 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:bg-slate-100 active:text-slate-700 transition ease-in-out duration-150"
                    >Previous</a
                >
                <a
                    href="#"
                    @click.stop="changePageUrl(pagination.next_page_url)"
                    class="ml-3 relative inline-flex items-center px-4 py-2 border border-slate-300 text-sm leading-5 font-medium rounded-md text-slate-700 bg-white hover:text-slate-500 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:bg-slate-100 active:text-slate-700 transition ease-in-out duration-150"
                    >Next</a
                >
            </div>
            <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                    <p class="text-sm leading-5 text-slate-700">
                        Showing
                        <span class="font-medium">{{ pagination.current_page }}</span>
                        to
                        <span class="font-medium">{{ pagination.last_page }}</span>
                        of
                        <span class="font-medium">{{ pagination.total }}</span>
                        results
                    </p>
                </div>
                <div>
                    <span class="relative z-0 inline-flex shadow-sm">
                        <button
                            type="button"
                            @click.stop="changePageUrl(pagination.prev_page_url)"
                            class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-slate-300 bg-white text-sm leading-5 font-medium text-slate-500 hover:text-slate-400 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-slate-100 active:text-slate-500 transition ease-in-out duration-150"
                        >
                            <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    class="chevron-left-icon"
                                    fill-rule="evenodd"
                                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </button>

                        <button
                            v-for="page in pages"
                            :key="page"
                            @click.stop="changePageNumber(page)"
                            type="button"
                            :class="
                                page === pagination.current_page
                                    ? 'text-blue-700 hover:text-blue-500 bg-slate-100'
                                    : 'bg-white text-slate-700 hover:text-slate-500'
                            "
                            class="-ml-px relative inline-flex items-center px-4 py-2 border border-slate-300 text-sm leading-5 font-medium focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-slate-100 active:text-slate-700 transition ease-in-out duration-150"
                        >
                            {{ page }}
                        </button>

                        <span
                            class="-ml-px relative inline-flex items-center px-4 py-2 border border-slate-300 bg-white text-sm leading-5 font-medium text-slate-700"
                            >...</span
                        >

                        <button
                            type="button"
                            @click.stop="changePageUrl(pagination.last_page_url)"
                            :class="
                                pagination.last_page === pagination.current_page
                                    ? 'text-blue-700 hover:text-blue-500 bg-slate-100'
                                    : 'bg-white text-slate-700 hover:text-slate-500'
                            "
                            class="-ml-px relative inline-flex items-center px-4 py-2 border border-slate-300 bg-white text-sm leading-5 font-medium text-slate-700 hover:text-slate-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-slate-100 active:text-slate-700 transition ease-in-out duration-150"
                        >
                            {{ pagination.last_page }}
                        </button>

                        <button
                            type="button"
                            @click.stop="changePageUrl(pagination.next_page_url)"
                            class="-ml-px relative inline-flex items-center px-2 py-2 rounded-r-md border border-slate-300 bg-white text-sm leading-5 font-medium text-slate-500 hover:text-slate-400 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-slate-100 active:text-slate-500 transition ease-in-out duration-150"
                        >
                            <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </button>
                    </span>
                </div>
            </div>
        </div>
        <div class="w-full grid place-items-center">
            <TableRowDataLoader :loading="loading" />
        </div>
    </div>
</template>
<style scoped>
.td-block-wrap {
    display: block !important;
}

.tbody .tr {
    display: -webkit-inline-box;
}

.rotate-180 {
    transform: rotate(180deg);
}
</style>
